import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShieldCheck } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Blocking consent modal shown to any authenticated user who hasn't yet
 * accepted the current Terms and Privacy Policy. Writes the acceptance
 * to the backend which stores a timestamp + the version (effective_date)
 * of each document at the time of consent.
 */
const ConsentModal = () => {
  const [needsConsent, setNeedsConsent] = useState(false);
  const [checkingConsent, setCheckingConsent] = useState(true);
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        if (!res.data.terms_accepted_at) {
          setNeedsConsent(true);
        }
      } catch (err) {
        // If not authenticated here, Dashboard protected route will redirect; don't block.
      } finally {
        setCheckingConsent(false);
      }
    };
    check();
  }, []);

  const handleAccept = async () => {
    if (!agreeTerms || !agreePrivacy) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/auth/accept-terms`, {}, { withCredentials: true });
      setNeedsConsent(false);
    } catch (err) {
      console.error('Failed to record consent:', err);
      setError('We could not record your acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (checkingConsent || !needsConsent) return null;

  return (
    <Dialog open={needsConsent} onOpenChange={() => { /* block outside close */ }}>
      <DialogContent
        className="sm:max-w-lg"
        // Prevent closing via ESC or backdrop — consent is required to use the app
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="consent-modal"
      >
        <DialogHeader>
          <div className="flex items-center space-x-3 mb-2">
            <div className="p-2 bg-brand-accent/10 rounded-full">
              <ShieldCheck className="w-6 h-6 text-brand-accent" />
            </div>
            <DialogTitle className="text-xl">Before you continue</DialogTitle>
          </div>
          <DialogDescription className="text-gray-700 leading-relaxed">
            We&apos;ve updated how we present our legal terms. To keep using ReflectiveMind,
            please confirm you&apos;ve read and agree to the documents below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <label className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-brand-accent/5 cursor-pointer transition-colors">
            <input
              data-testid="consent-terms-checkbox"
              type="checkbox"
              checked={agreeTerms}
              onChange={(e) => setAgreeTerms(e.target.checked)}
              className="mt-1 h-4 w-4 text-brand-accent rounded border-gray-300 focus:ring-brand-accent"
            />
            <span className="text-sm text-gray-800">
              I have read and agree to the{' '}
              <Link
                to="/terms"
                target="_blank"
                className="text-brand-accent font-semibold underline underline-offset-2"
                data-testid="consent-terms-link"
              >
                Terms of Service
              </Link>
              .
            </span>
          </label>

          <label className="flex items-start space-x-3 p-3 rounded-lg border border-gray-200 hover:bg-brand-accent/5 cursor-pointer transition-colors">
            <input
              data-testid="consent-privacy-checkbox"
              type="checkbox"
              checked={agreePrivacy}
              onChange={(e) => setAgreePrivacy(e.target.checked)}
              className="mt-1 h-4 w-4 text-brand-accent rounded border-gray-300 focus:ring-brand-accent"
            />
            <span className="text-sm text-gray-800">
              I acknowledge the{' '}
              <Link
                to="/privacy"
                target="_blank"
                className="text-brand-accent font-semibold underline underline-offset-2"
                data-testid="consent-privacy-link"
              >
                Privacy Policy
              </Link>{' '}
              and consent to the processing of my data as described.
            </span>
          </label>
        </div>

        {error && (
          <p className="text-sm text-red-600 mt-2" data-testid="consent-error">
            {error}
          </p>
        )}

        <div className="mt-4 flex items-center justify-end">
          <Button
            data-testid="consent-accept-btn"
            onClick={handleAccept}
            disabled={!agreeTerms || !agreePrivacy || submitting}
            className="bg-brand-cta hover:bg-brand-cta-hover text-white font-semibold px-6"
          >
            {submitting ? 'Saving…' : 'Accept & Continue'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConsentModal;
